import React, { ComponentPropsWithoutRef, CSSProperties } from "react"

import { cn } from "@/utils/cn"

interface RippleProps extends ComponentPropsWithoutRef<"div"> {
  mainCircleSize?: number
  mainCircleOpacity?: number
  numCircles?: number
  /**
   * Position of the ripple origin
   * - 'center': Ripples emanate from the center (default)
   * - 'bottom': Ripples emanate from the bottom, showing only top half
   */
  position?: 'center' | 'bottom'
}

export const Ripple = React.memo(function Ripple({
  mainCircleSize = 210,
  mainCircleOpacity = 0.12,
  numCircles = 8,
  position = 'center',
  className,
  ...props
}: RippleProps) {
  // Position-specific configurations
  const isBottom = position === 'bottom'
  const maskGradient = isBottom
    ? '[mask-image:linear-gradient(to_top,white,transparent)]'
    : '[mask-image:linear-gradient(to_bottom,white,transparent)]'

  return (
    <div
      className={cn(
        "pointer-events-none absolute inset-0 select-none",
        maskGradient,
        className
      )}
      {...props}
    >
      {Array.from({ length: numCircles }, (_, i) => {
        const size = mainCircleSize + i * 70
        const opacity = mainCircleOpacity - i * 0.01
        const animationDelay = `${i * 0.01}s`
        const borderStyle = "solid"

        return (
          <div
            key={i}
            className="absolute rounded-full border bg-foreground/10"
            style={
              {
                width: `${size}px`,
                height: `${size}px`,
                opacity,
                animationDelay,
                borderStyle,
                borderWidth: "0.25px",
                borderColor: `hsl(var(--foreground))`,
                top: isBottom ? "100%" : "50%",
                left: "50%",
                animation: `ripple-${position} 5s ease-out ${i * 0.4}s infinite`,
              } as CSSProperties
            }
          />
        )
      })}
    </div>
  )
})

Ripple.displayName = "Ripple"
